import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Badge, Form, Row, Col, Table } from 'react-bootstrap';
import Select from 'react-select';
import { listarClientesDisponiveisEtiquetas } from '~/Services/Service';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';

const TAMANHO_PAGINA = 20;

const itemKey = (item) =>
  `${item.codigo_conta}-${item.codigo_grupo_empresa}-${item.codigo_ativo != null ? item.codigo_ativo : 0}`;

const tipoLabel = (tipo) => {
  if (!tipo) return '';
  const mapa = {
    C: 'Clientes',
    F: 'Fornecedores',
    I: 'Imóveis',
    P: 'Prospects',
  };
  return mapa[tipo] ? mapa[tipo] : tipo;
};

const ModalSelecaoIndividual = ({
  show,
  onHide,
  filtros,
  selecionados,
  onSelecionadosChange,
  gruposEmpresa,
  onEmitir,
  emitindo,
}) => {
  const [disponiveis, setDisponiveis] = useState([]);
  const [totalDisponiveis, setTotalDisponiveis] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [buscaInput, setBuscaInput] = useState('');
  const [geModal, setGeModal] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const totalPaginas = Math.ceil(totalDisponiveis / TAMANHO_PAGINA) || 1;
  const codigoGrupoEmpresaModal = geModal ? geModal.value : 0;

  const carregarDisponiveis = useCallback(() => {
    if (!show) return;
    setCarregando(true);
    listarClientesDisponiveisEtiquetas({
      ...filtros,
      codigo_grupo_empresa: codigoGrupoEmpresaModal,
      pagina: pagina - 1, // SQL usa OFFSET (@Pagina * @TamanhoPagina) — 0-based
      tamanho_pagina: TAMANHO_PAGINA,
      busca,
    })
      .then((res) => {
        setDisponiveis(res && res.itens ? res.itens : []);
        setTotalDisponiveis(res && res.total ? res.total : 0);
      })
      .catch(() => {
        setDisponiveis([]);
        setTotalDisponiveis(0);
      })
      .finally(() => setCarregando(false));
  }, [show, filtros, codigoGrupoEmpresaModal, pagina, busca]);

  useEffect(() => {
    carregarDisponiveis();
  }, [carregarDisponiveis]);

  useEffect(() => {
    if (show) {
      setPagina(1);
      setBusca('');
      setBuscaInput('');
      const codigoDefault = filtros && filtros.codigo_grupo_empresa;
      const geDefault = codigoDefault
        ? (gruposEmpresa || []).find((g) => g.value === codigoDefault) || null
        : null;
      setGeModal(geDefault);
    }
  }, [show, filtros, gruposEmpresa]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagina(1);
      setBusca(buscaInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [buscaInput]);

  const handleAdicionar = (item) => {
    const key = itemKey(item);
    const jaExiste = selecionados.some((s) => itemKey(s) === key);
    if (jaExiste) return;
    const novoItem = {
      codigo_conta: item.codigo_conta,
      codigo_grupo_empresa: item.codigo_grupo_empresa,
      codigo_ativo: item.codigo_ativo != null ? item.codigo_ativo : 0,
      razao_social: item.razao_social,
      origem: item.origem != null ? item.origem : null,
      ordem: selecionados.length + 1,
    };
    onSelecionadosChange([...selecionados, novoItem]);
  };

  const handleRemover = (item) => {
    const key = itemKey(item);
    const nova = selecionados
      .filter((s) => !(itemKey(s) === key && s.ordem === item.ordem))
      .map((s, idx) => ({ ...s, ordem: idx + 1 }));
    onSelecionadosChange(nova);
  };

  const handleLimpar = () => {
    onSelecionadosChange([]);
  };

  const handleGeChange = (opcao) => {
    setGeModal(opcao);
    setPagina(1);
    setBusca('');
    setBuscaInput('');
  };

  const handleEmitir = () => {
    if (typeof onEmitir === 'function') {
      onEmitir(selecionados);
    }
  };

  const irParaPagina = (novaPagina) => {
    if (novaPagina < 1 || novaPagina > totalPaginas) return;
    setPagina(novaPagina);
  };

  const totalFormulario = selecionados.length;

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Seleção Individual
          {filtros && filtros.tipo_emissao ? (
            <span className="ml-2 text-muted" style={{ fontSize: '0.9em' }}>
              — {tipoLabel(filtros.tipo_emissao)}
            </span>
          ) : null}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-2 align-items-center">
          <Col md={4}>
            <Select
              placeholder="Selecione o GE..."
              isClearable
              options={gruposEmpresa || []}
              value={geModal}
              onChange={handleGeChange}
              styles={customStyles}
              components={{ DropdownIndicator }}
              noOptionsMessage={() => 'Nenhum GE encontrado'}
            />
          </Col>
          <Col md={5}>
            <Form.Control
              size="sm"
              type="text"
              placeholder="Buscar por nome ou código..."
              value={buscaInput}
              onChange={(e) => setBuscaInput(e.target.value)}
            />
          </Col>
          <Col md={3} className="text-right">
            <Badge variant="secondary" className="mr-2">
              Total de clientes: {totalDisponiveis}
            </Badge>
            <Badge variant="info">
              Total de formulário: {totalFormulario}
            </Badge>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <strong>Disponíveis</strong>
            <Table striped bordered hover size="sm" className="mt-1">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Apelido</th>
                  <th>GE</th>
                  <th>QD/LT</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {carregando ? (
                  <tr>
                    <td colSpan={5} className="text-center">
                      Carregando...
                    </td>
                  </tr>
                ) : disponiveis.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      Nenhum registro encontrado.
                    </td>
                  </tr>
                ) : (
                  disponiveis.map((item) => {
                    const key = itemKey(item);
                    const jaSelecionado = selecionados.some(
                      (s) => itemKey(s) === key
                    );
                    return (
                      <tr
                        key={key}
                        onDoubleClick={() => !jaSelecionado && handleAdicionar(item)}
                        style={{ cursor: 'pointer' }}
                        className={jaSelecionado ? 'table-success' : ''}
                      >
                        <td>{item.codigo_conta}</td>
                        <td>{item.razao_social}</td>
                        <td>{item.codigo_grupo_empresa}</td>
                        <td>{item.quadra_lote}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            disabled={jaSelecionado}
                            onClick={() => handleAdicionar(item)}
                            style={{ padding: '0 6px', fontSize: '0.75rem' }}
                          >
                            +
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>

            <div className="d-flex align-items-center justify-content-between">
              <small className="text-muted">
                Pág. {pagina} / {totalPaginas} &nbsp;({totalDisponiveis} registros)
              </small>
              <div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="mr-1"
                  disabled={pagina <= 1}
                  onClick={() => irParaPagina(1)}
                >
                  «
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="mr-1"
                  disabled={pagina <= 1}
                  onClick={() => irParaPagina(pagina - 1)}
                >
                  ‹
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="mr-1"
                  disabled={pagina >= totalPaginas}
                  onClick={() => irParaPagina(pagina + 1)}
                >
                  ›
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={pagina >= totalPaginas}
                  onClick={() => irParaPagina(totalPaginas)}
                >
                  »
                </Button>
              </div>
            </div>
          </Col>

          <Col md={6}>
            <strong>Selecionados</strong>
            <Table striped bordered hover size="sm" className="mt-1">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th>Código</th>
                  <th>Apelido</th>
                  <th>GE</th>
                  <th style={{ width: 70 }}></th>
                </tr>
              </thead>
              <tbody>
                {selecionados.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      Nenhum selecionado.
                    </td>
                  </tr>
                ) : (
                  selecionados.map((item) => (
                    <tr key={`sel-${itemKey(item)}-${item.ordem}`}>
                      <td>{item.ordem}</td>
                      <td>{item.codigo_conta}</td>
                      <td>{item.razao_social}</td>
                      <td>{item.codigo_grupo_empresa}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleRemover(item)}
                          style={{ padding: '0 6px', fontSize: '0.75rem' }}
                        >
                          ×
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col className="d-flex justify-content-end">
            <Button
              variant="outline-secondary"
              className="mr-2"
              onClick={handleLimpar}
              disabled={emitindo || selecionados.length === 0}
            >
              Limpar
            </Button>
            <Button
              variant="success"
              onClick={handleEmitir}
              disabled={emitindo || selecionados.length === 0}
            >
              {emitindo ? 'Emitindo...' : 'Emitir Selecionados'}
            </Button>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={emitindo}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalSelecaoIndividual;
